import { Lead, GuideSection, H3, P, UL, OL, LI, GuideLink } from './guide-ui';

/**
 * Nederlandstalige gids (locale: 'nl', logpulse.nl): NIS2 en de
 * Cyberbeveiligingswet voor Nederlandse organisaties. Bewust een origineel
 * lokaal stuk (Cbw, RDI, NCSC, Nederlandse sectoren en boetes), geen
 * vertaling van de Engelse NIS2-gids — die behandelt de richtlijn zelf en
 * blijft via de related links bereikbaar voor verdieping.
 */
export function Nis2CyberbeveiligingswetGuideBody() {
  return (
    <>
      <Lead>
        NIS2 is de Europese cyberbeveiligingsrichtlijn die in Nederland is
        ingevoerd via de Cyberbeveiligingswet, per 15 augustus 2026 van kracht.
        Ruim 8.000 Nederlandse organisaties die nooit eerder onder
        cyberwetgeving vielen, krijgen er een zorgplicht, een meldplicht en
        persoonlijke verantwoordelijkheid voor het bestuur bij. Deze gids legt
        uit wie eronder valt, wat de wet vraagt, en wat dat concreet betekent
        voor je logging en monitoring.
      </Lead>

      <GuideSection id="wat-is-nis2" title="Van NIS2-richtlijn naar Cyberbeveiligingswet">
        <P>
          NIS2 (Richtlijn (EU) 2022/2555) is de opvolger van de eerste
          NIS-richtlijn en moest uiterlijk 17 oktober 2024 in nationale
          wetgeving zijn omgezet. Nederland doet dat met de{' '}
          <strong>Cyberbeveiligingswet (Cbw)</strong>, de opvolger van de Wet
          beveiliging netwerk- en informatiesystemen (Wbni). Die deadline is
          niet gehaald, maar de wet is er nu wel:{' '}
          <strong>de Cyberbeveiligingswet is per 15 augustus 2026 van kracht</strong>.
        </P>
        <P>
          Er is geen overgangsperiode. Vanaf die datum gelden de zorgplicht, de
          meldplicht en de registratieplicht bij het NCSC direct, en is het
          bestuur er persoonlijk verantwoordelijk voor. Dat maakt de vraag niet
          langer of je je voorbereidt, maar of je vandaag kunt aantonen dat je
          risico&rsquo;s beheerst en of je een incident binnen 24 uur zou zien
          en kunnen melden.
        </P>
        <P>
          De grootste verschuiving ten opzichte van de Wbni: die wet raakte
          enkele honderden vitale aanbieders. De Cyberbeveiligingswet raakt
          ruim 8.000 Nederlandse organisaties in achttien sectoren, inclusief
          middelgrote bedrijven die zichzelf nooit als &ldquo;vitaal&rdquo;
          hebben gezien.
        </P>
      </GuideSection>

      <GuideSection id="val-ik-eronder" title="Val ik onder NIS2?">
        <P>
          De wet kent twee categorieën, met verschillend toezicht en
          verschillende boetes:
        </P>
        <UL>
          <LI>
            <strong>Essentiële entiteiten</strong>: grote organisaties in
            sectoren als energie, transport, bankwezen, financiële
            marktinfrastructuur, zorg, drinkwater en afvalwater, digitale
            infrastructuur, overheidsdiensten en ruimtevaart.
          </LI>
          <LI>
            <strong>Belangrijke entiteiten</strong>: middelgrote en grote
            organisaties in onder meer post en koeriersdiensten, afvalbeheer,
            chemie, levensmiddelen, industrie (waaronder medische hulpmiddelen
            en elektronica), digitale aanbieders en onderzoek.
          </LI>
        </UL>
        <P>
          De omvangsgrens ligt in de basis bij circa 50 medewerkers of 10
          miljoen euro jaaromzet. Kleinere organisaties kunnen er toch onder
          vallen, bijvoorbeeld als enige aanbieder van een essentiële dienst
          of als onderdeel van een groter concern. Er geldt bovendien een{' '}
          <strong>registratieplicht</strong>: organisaties die onder de wet
          vallen moeten zich registreren bij de toezichthouder.
        </P>
        <P>
          Twijfel je? De Rijksoverheid biedt een online{' '}
          <strong>NIS2-zelfevaluatie</strong> waarmee je in enkele minuten
          formeel toetst of je organisatie onder de wet valt en in welke
          categorie.
        </P>
      </GuideSection>

      <GuideSection id="zorgplicht" title="De zorgplicht: welke maatregelen moet je nemen?">
        <P>
          De kern van de wet is de zorgplicht: passende en evenredige
          technische en organisatorische maatregelen om de risico&rsquo;s voor
          je netwerk- en informatiesystemen te beheersen. De richtlijn noemt
          expliciet onder meer:
        </P>
        <UL>
          <LI>risicoanalyse en beleid voor informatiebeveiliging;</LI>
          <LI>
            <strong>incidentafhandeling</strong>: kunnen detecteren,
            analyseren, beperken en herstellen, en dat kunnen aantonen;
          </LI>
          <LI>bedrijfscontinuïteit, back-ups en crisisbeheer;</LI>
          <LI>beveiliging van de toeleveringsketen (supply chain);</LI>
          <LI>beveiliging bij ontwikkeling, aanschaf en onderhoud van systemen;</LI>
          <LI>beleid om de effectiviteit van maatregelen te meten;</LI>
          <LI>cyberhygiëne en security-training;</LI>
          <LI>cryptografie en waar passend encryptie;</LI>
          <LI>toegangsbeveiliging, personeelsbeveiliging en asset-beheer;</LI>
          <LI>multifactorauthenticatie en beveiligde communicatie.</LI>
        </UL>
        <P>
          Nieuw ten opzichte van de oude situatie is de{' '}
          <strong>persoonlijke verantwoordelijkheid van het bestuur</strong>:
          bestuurders moeten de maatregelen goedkeuren, toezien op de
          uitvoering, en zelf een cybersecurity-opleiding volgen. Bij ernstige
          nalatigheid kunnen bestuurders persoonlijk aansprakelijk worden
          gesteld.
        </P>
      </GuideSection>

      <GuideSection id="meldplicht" title="De meldplicht: 24 uur, 72 uur, één maand">
        <P>
          Significante incidenten moet je melden bij het nationale CSIRT (voor
          de meeste organisaties het NCSC) en de toezichthouder, in drie
          stappen:
        </P>
        <OL>
          <LI>
            <strong>Binnen 24 uur</strong>: een vroegtijdige waarschuwing, met
            een eerste inschatting of er sprake is van kwaadwillig handelen en
            of het incident grensoverschrijdende gevolgen kan hebben.
          </LI>
          <LI>
            <strong>Binnen 72 uur</strong>: een volledige incidentmelding met
            een eerste beoordeling van ernst, impact en, waar beschikbaar,
            indicatoren van compromittering (IOC&rsquo;s).
          </LI>
          <LI>
            <strong>Binnen één maand</strong>: een eindrapport met een
            gedetailleerde beschrijving van het incident, de vermoedelijke
            oorzaak, de genomen maatregelen en de grensoverschrijdende impact.
          </LI>
        </OL>
        <P>
          Die termijnen zijn kort. Wie pas na een incident begint met logs
          verzamelen, haalt ze niet: binnen 24 uur iets zinnigs zeggen over
          kwaadwillig handelen vereist dat de relevante logs er al zijn,
          doorzoekbaar zijn, en dat detectie het incident überhaupt heeft
          opgemerkt.
        </P>
      </GuideSection>

      <GuideSection id="toezicht-en-boetes" title="Toezicht, boetes en aansprakelijkheid">
        <P>
          De <strong>Rijksinspectie Digitale Infrastructuur (RDI)</strong> is
          voor veel sectoren de toezichthouder; daarnaast houden sectorale
          toezichthouders toezicht op hun eigen domein, zoals DNB voor de
          financiële sector. Essentiële entiteiten krijgen proactief toezicht
          (audits en inspecties vooraf); belangrijke entiteiten reactief
          toezicht (na signalen of incidenten).
        </P>
        <P>De boetes zijn van AVG-kaliber:</P>
        <UL>
          <LI>
            essentiële entiteiten: tot <strong>10 miljoen euro of 2%</strong>{' '}
            van de wereldwijde jaaromzet;
          </LI>
          <LI>
            belangrijke entiteiten: tot <strong>7 miljoen euro of 1,4%</strong>{' '}
            van de wereldwijde jaaromzet.
          </LI>
        </UL>
        <P>
          Daarnaast kan de toezichthouder bindende aanwijzingen geven, en bij
          essentiële entiteiten in het uiterste geval bestuurders tijdelijk
          uit hun functie laten ontheffen.
        </P>
      </GuideSection>

      <GuideSection id="logging-en-monitoring" title="Wat dit concreet betekent voor logging en monitoring">
        <P>
          Logging en monitoring komen in bijna elke verplichting terug: je
          kunt geen incident detecteren, geen 24-uursmelding onderbouwen en
          geen eindrapport schrijven zonder de juiste logs. Praktisch komt de
          zorgplicht hierop neer:
        </P>
        <H3>Centraliseer je logs</H3>
        <P>
          Logs van servers, netwerkapparatuur, identity providers,
          cloud-diensten en applicaties horen in één doorzoekbaar systeem. Een
          incident reconstrueren over tien losse systemen heen lukt niet
          binnen 72 uur.
        </P>
        <H3>Bewaar ze lang genoeg, aantoonbaar</H3>
        <P>
          De wet noemt geen vaste termijn; het criterium is dat je incidenten
          kunt onderzoeken en rapporteren. In de praktijk is 6 tot 18 maanden
          gangbaar, met een doorzoekbare hot-laag en een goedkoper archief.
          Leg je keuze en onderbouwing vast, dat is precies waar een
          toezichthouder naar vraagt.
        </P>
        <H3>Monitor realtime, niet achteraf</H3>
        <P>
          De meldtermijnen veronderstellen dat je incidenten zíet gebeuren.
          Dat vraagt om detectieregels op je logs (mislukte inlogpogingen,
          afwijkend gedrag, verdachte netwerkverbindingen) en om alerting die
          een mens op tijd bereikt, zonder te verdrinken in vals alarm.
        </P>
        <H3>Houd een audit trail bij</H3>
        <P>
          Wie heeft wat wanneer gedaan, ook in je logplatform zelf? Een
          manipulatiebestendige audit trail onderbouwt zowel je
          incidentrapportage als je verantwoording richting toezichthouder.
        </P>
        <P>
          Dit is de kant die LogPulse afdekt: gecentraliseerd logbeheer met
          instelbare retentie per plan, 50+ ingebouwde detecties met MITRE
          ATT&amp;CK-mapping, risicogebaseerde alerting die een handvol echte
          incidenten oplevert in plaats van duizenden meldingen, en
          compliance-rapportages die je detecties en data aan
          NIS2-verplichtingen koppelen. Alle data blijft in de EU (GCP
          Amsterdam), wat de verantwoording richting toezichthouder en
          verwerkersafspraken aanzienlijk versimpelt.
        </P>
      </GuideSection>

      <GuideSection id="stappenplan" title="Stappenplan: aan de slag">
        <OL>
          <LI>
            <strong>Stel vast of je onder de wet valt</strong> via de
            NIS2-zelfevaluatie van de Rijksoverheid, en in welke categorie.
          </LI>
          <LI>
            <strong>Informeer het bestuur</strong>: zij zijn straks persoonlijk
            verantwoordelijk en moeten de aanpak goedkeuren.
          </LI>
          <LI>
            <strong>Doe een risicoanalyse</strong> en breng in kaart welke
            systemen en leveranciers kritiek zijn voor je dienstverlening.
          </LI>
          <LI>
            <strong>Centraliseer logging en richt detectie in</strong>: dit is
            de fundering onder zowel de zorgplicht als de meldplicht.
          </LI>
          <LI>
            <strong>Oefen de meldprocedure</strong>: weet wie binnen 24 uur het
            NCSC en de toezichthouder informeert, en met welke informatie.
          </LI>
          <LI>
            <strong>Documenteer alles</strong>: maatregelen, keuzes,
            retentietermijnen en oefeningen. Compliance die je niet kunt
            aantonen, bestaat voor een toezichthouder niet.
          </LI>
        </OL>
        <P>
          Meer weten over hoe LogPulse de logging-, detectie- en
          rapportagekant invult? Zie{' '}
          <GuideLink href="/compliance/nis2">NIS2-compliance met LogPulse</GuideLink>{' '}
          of start gratis en richt je eerste detecties vandaag in.
        </P>
      </GuideSection>
    </>
  );
}
